import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Slider } from '../components/ui/slider';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Calculator, ArrowRight, Sun, Home, Zap, Leaf } from 'lucide-react';

const ORIENTATION_FACTORS = {
  south: 1,
  southeast: 0.95,
  southwest: 0.95,
  east: 0.85,
  west: 0.85,
};

const PANEL_AREA_M2 = 2;
const PANEL_POWER_KW = 0.45;
const BASE_YIELD_KWH_PER_KWP = 1650;
const SELF_CONSUMPTION_FACTOR = 0.75;
const GRID_PRICE_PER_KWH = 0.24;
const EXCESS_COMPENSATION_PER_KWH = 0.08;
const SYSTEM_COST_PER_KWP = 1200;
const FIXED_INSTALL_COST = 1800;
const GRANT_FACTOR = 0.3;
const PROPERTY_VALUE_FACTOR = 0.015;

function calculateSolarEstimate({ monthlyConsumption, bill, space, orientation }) {
  const annualConsumption = monthlyConsumption * 12;
  const roofFactor = ORIENTATION_FACTORS[orientation] ?? 0.9;

  const maxPanelsBySpace = Math.max(1, Math.floor(space / PANEL_AREA_M2));
  const maxPowerBySpace = maxPanelsBySpace * PANEL_POWER_KW;

  const targetPowerByConsumption = annualConsumption / (BASE_YIELD_KWH_PER_KWP * roofFactor);
  const recommendedPower = Math.max(PANEL_POWER_KW, Math.min(maxPowerBySpace, targetPowerByConsumption));
  const recommendedPanels = Math.max(1, Math.floor(recommendedPower / PANEL_POWER_KW));
  const actualPower = recommendedPanels * PANEL_POWER_KW;

  const annualProduction = Math.round(actualPower * BASE_YIELD_KWH_PER_KWP * roofFactor);
  const selfConsumedEnergy = Math.min(annualProduction * SELF_CONSUMPTION_FACTOR, annualConsumption);
  const excessEnergy = Math.max(0, annualProduction - selfConsumedEnergy);
  const annualSavings = Math.round(selfConsumedEnergy * GRID_PRICE_PER_KWH + excessEnergy * EXCESS_COMPENSATION_PER_KWH);
  const monthlySavings = Math.round(annualSavings / 12);

  const coverage = annualConsumption > 0 ? Math.min(100, Math.round((annualProduction / annualConsumption) * 100)) : 0;
  const currentAnnualBill = bill * 12;
  const savingsPercentage = currentAnnualBill > 0 ? Math.min(100, Math.round((annualSavings / currentAnnualBill) * 100)) : 0;

  const grossSystemCost = Math.round(actualPower * SYSTEM_COST_PER_KWP + FIXED_INSTALL_COST);
  const estimatedAid = Math.round(grossSystemCost * GRANT_FACTOR);
  const netSystemCost = Math.max(2500, grossSystemCost - estimatedAid);
  const amortizationYears = annualSavings > 0 ? (netSystemCost / annualSavings).toFixed(1) : null;
  const propertyValueIncrease = Math.round(netSystemCost * PROPERTY_VALUE_FACTOR + 6000);
  const total25YearSavings = annualSavings * 25 + propertyValueIncrease;
  const co2Reduction = Math.round(annualProduction * 0.18);

  return {
    monthlySavings,
    annualSavings,
    savingsPercentage,
    annualConsumption,
    annualProduction,
    recommendedPanels,
    recommendedPower: Number(actualPower.toFixed(2)),
    maxPanelsBySpace,
    coverage,
    grossSystemCost,
    estimatedAid,
    netSystemCost,
    amortization: amortizationYears ? `${amortizationYears} años` : 'A consultar',
    total25YearSavings,
    co2Reduction,
  };
}

const CalculadoraSolar = () => {
  const [consumption, setConsumption] = useState([300]);
  const [bill, setBill] = useState([120]);
  const [space, setSpace] = useState([30]);
  const [orientation, setOrientation] = useState('south');

  const savings = useMemo(
    () =>
      calculateSolarEstimate({
        monthlyConsumption: consumption[0],
        bill: bill[0],
        space: space[0],
        orientation,
      }),
    [consumption, bill, space, orientation]
  );

  return (
    <div className="min-h-screen">
      <section className="py-24 bg-nubia-gray-light">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <div className="w-20 h-20 bg-nubia-gold-soft/20 rounded-full flex items-center justify-center mx-auto mb-8">
              <Calculator className="w-10 h-10 text-nubia-gold-primary" />
            </div>
            <h1 className="text-5xl md:text-6xl font-bold text-nubia-green-primary mb-6">
              Calculadora Solar
            </h1>
            <p className="text-xl text-nubia-text-dark/70 leading-relaxed">
              Haz una preestimación realista según tu consumo, espacio disponible y orientación del tejado.
            </p>
          </div>
        </div>
      </section>

      <section className="py-24 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-8">
              <Card className="nubia-shadow border-2 border-nubia-gray-light">
                <CardHeader>
                  <CardTitle className="text-3xl text-nubia-green-primary">Tus Datos</CardTitle>
                  <CardDescription className="text-base">
                    Ajusta las variables principales y verás una estimación más coherente.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-8">
                  <div>
                    <label className="text-sm font-semibold text-nubia-green-primary mb-3 block flex items-center gap-2">
                      <Zap className="w-4 h-4" />
                      Consumo mensual: {consumption[0]} kWh
                    </label>
                    <Slider value={consumption} onValueChange={setConsumption} min={100} max={1500} step={10} className="w-full" />
                    <div className="flex justify-between text-xs text-nubia-text-dark/50 mt-2">
                      <span>100 kWh</span>
                      <span>1500 kWh</span>
                    </div>
                  </div>

                  <div>
                    <label className="text-sm font-semibold text-nubia-green-primary mb-3 block">
                      Factura actual: {bill[0]} €/mes
                    </label>
                    <Slider value={bill} onValueChange={setBill} min={40} max={500} step={5} className="w-full" />
                    <div className="flex justify-between text-xs text-nubia-text-dark/50 mt-2">
                      <span>40 €</span>
                      <span>500 €</span>
                    </div>
                  </div>

                  <div>
                    <label className="text-sm font-semibold text-nubia-green-primary mb-3 block flex items-center gap-2">
                      <Home className="w-4 h-4" />
                      Espacio disponible: {space[0]} m²
                    </label>
                    <Slider value={space} onValueChange={setSpace} min={10} max={120} step={2} className="w-full" />
                    <div className="flex justify-between text-xs text-nubia-text-dark/50 mt-2">
                      <span>10 m²</span>
                      <span>120 m²</span>
                    </div>
                  </div>

                  <div>
                    <label className="text-sm font-semibold text-nubia-green-primary mb-3 block">
                      Orientación del tejado
                    </label>
                    <Select value={orientation} onValueChange={setOrientation}>
                      <SelectTrigger className="border-2">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="south">Sur (óptima)</SelectItem>
                        <SelectItem value="southeast">Sureste</SelectItem>
                        <SelectItem value="southwest">Suroeste</SelectItem>
                        <SelectItem value="east">Este</SelectItem>
                        <SelectItem value="west">Oeste</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="bg-nubia-gold-soft/10 border-2 border-nubia-gold-primary/30 rounded-lg p-4">
                    <p className="text-sm text-nubia-text-dark/70">
                      <strong className="text-nubia-green-primary">Nota:</strong> Esta calculadora ya tiene en cuenta orientación y espacio, pero sigue siendo una preestimación. El estudio definitivo debe revisar sombras, tipo de cubierta, hábitos de consumo y ayudas reales.
                    </p>
                  </div>
                </CardContent>
              </Card>

              <Card className="nubia-shadow border-2 border-nubia-gold-primary/30 bg-gradient-to-br from-nubia-white to-nubia-gray-light">
                <CardHeader>
                  <CardTitle className="text-3xl text-nubia-green-primary">Tu Estimación Solar</CardTitle>
                  <CardDescription className="text-base">
                    Un cálculo orientativo más útil para entender tamaño, ahorro y retorno.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="bg-white rounded-xl p-6 nubia-shadow">
                    <div className="text-6xl font-bold text-nubia-gold-primary mb-2">{savings.monthlySavings}€</div>
                    <div className="text-nubia-text-dark font-semibold text-lg">Ahorro mensual estimado</div>
                    <div className="text-sm text-nubia-green-support font-medium mt-1">
                      {savings.savingsPercentage}% menos sobre tu factura actual
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-white rounded-xl p-5 nubia-shadow text-center">
                      <div className="text-3xl font-bold text-nubia-green-primary mb-1">{savings.recommendedPanels}</div>
                      <div className="text-sm text-nubia-text-dark/60">Paneles estimados</div>
                    </div>
                    <div className="bg-white rounded-xl p-5 nubia-shadow text-center">
                      <div className="text-3xl font-bold text-nubia-green-primary mb-1">{savings.recommendedPower} kWp</div>
                      <div className="text-sm text-nubia-text-dark/60">Potencia recomendada</div>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-white rounded-xl p-5 nubia-shadow text-center">
                      <div className="text-3xl font-bold text-nubia-green-primary mb-1">{savings.annualProduction.toLocaleString()} kWh</div>
                      <div className="text-sm text-nubia-text-dark/60">Producción anual estimada</div>
                    </div>
                    <div className="bg-white rounded-xl p-5 nubia-shadow text-center">
                      <div className="text-3xl font-bold text-nubia-green-primary mb-1">{savings.coverage}%</div>
                      <div className="text-sm text-nubia-text-dark/60">Cobertura del consumo</div>
                    </div>
                  </div>

                  <div className="bg-nubia-green-primary rounded-xl p-6 text-white">
                    <div className="text-4xl font-bold text-nubia-gold-primary mb-2">
                      {savings.total25YearSavings.toLocaleString()}€
                    </div>
                    <div className="text-lg font-semibold mb-2">Valor acumulado a 25 años</div>
                    <p className="text-sm text-nubia-white/80">
                      Combina ahorro eléctrico estimado y revalorización potencial del inmueble.
                    </p>
                  </div>

                  <div className="bg-white rounded-xl p-5 nubia-shadow space-y-2">
                    <div className="flex justify-between gap-4 text-sm">
                      <span className="text-nubia-text-dark/70">Coste orientativo de instalación</span>
                      <strong>{savings.grossSystemCost.toLocaleString()}€</strong>
                    </div>
                    <div className="flex justify-between gap-4 text-sm">
                      <span className="text-nubia-text-dark/70">Ayudas estimadas</span>
                      <strong className="text-nubia-green-support">-{savings.estimatedAid.toLocaleString()}€</strong>
                    </div>
                    <div className="flex justify-between gap-4 text-sm border-t pt-2">
                      <span className="text-nubia-text-dark/70">Retorno estimado</span>
                      <strong>{savings.amortization}</strong>
                    </div>
                    <div className="flex justify-between gap-4 text-sm">
                      <span className="text-nubia-text-dark/70">Capacidad máxima por espacio</span>
                      <strong>{savings.maxPanelsBySpace} paneles</strong>
                    </div>
                  </div>

                  <div className="bg-nubia-green-support/10 border-2 border-nubia-green-support/30 rounded-xl p-5">
                    <div className="text-2xl font-bold text-nubia-green-support mb-1 flex items-center gap-2">
                      <Leaf className="w-5 h-5" />
                      {savings.co2Reduction} kg CO₂
                    </div>
                    <div className="text-sm text-nubia-text-dark/70">Reducción anual estimada de emisiones</div>
                  </div>

                  <div className="space-y-3 pt-4">
                    <Link to="/contacto">
                      <Button className="w-full bg-nubia-gold-primary hover:bg-nubia-gold-primary/90 text-nubia-green-primary font-semibold py-6 text-lg shadow-lg">
                        Solicitar presupuesto personalizado
                        <ArrowRight className="ml-2 w-5 h-5" />
                      </Button>
                    </Link>
                    <Link to="/beneficios">
                      <Button variant="outline" className="w-full border-2 border-nubia-green-primary text-nubia-green-primary hover:bg-nubia-green-primary hover:text-white py-6">
                        Ver todos los beneficios
                      </Button>
                    </Link>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      <section className="py-24 bg-nubia-gray-light">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold text-nubia-green-primary mb-8 text-center">
              ¿Cómo calculamos esta estimación?
            </h2>
            <div className="grid md:grid-cols-3 gap-6">
              <Card className="border-0 nubia-shadow">
                <CardHeader>
                  <CardTitle className="text-xl text-nubia-green-primary">Consumo real</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-nubia-text-dark/70">
                    Partimos de tu consumo mensual y tu factura para estimar ahorro, cobertura y retorno económico.
                  </p>
                </CardContent>
              </Card>
              <Card className="border-0 nubia-shadow">
                <CardHeader>
                  <CardTitle className="text-xl text-nubia-green-primary">Limitación por tejado</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-nubia-text-dark/70">
                    El espacio disponible y la orientación limitan la potencia instalable y la producción anual esperada.
                  </p>
                </CardContent>
              </Card>
              <Card className="border-0 nubia-shadow">
                <CardHeader>
                  <CardTitle className="text-xl text-nubia-green-primary">Rentabilidad</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-nubia-text-dark/70">
                    Estimamos inversión, ayudas orientativas, ahorro anual y amortización para que el resultado tenga sentido comercial.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      <section className="py-24 nubia-gradient text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">¿Quieres un estudio más preciso?</h2>
          <p className="text-xl mb-10 max-w-3xl mx-auto text-nubia-white/90">
            Nuestros expertos pueden revisar tu tejado, sombras, consumo y ayudas disponibles para darte una propuesta real.
          </p>
          <Link to="/contacto">
            <Button size="lg" className="bg-nubia-gold-primary hover:bg-nubia-gold-primary/90 text-nubia-green-primary font-semibold px-10 py-7 text-lg shadow-xl">
              Solicitar estudio gratuito
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default CalculadoraSolar;
